/**
 * Line-art emblems for the grouping roots (docs/gameplay/skill-tree-ui.md,
 * Grouping Roots), each modeled on its painted emblem. Drawn as outlines in
 * a box about +/-18 units around the origin: a soft glow, a crisp line in the
 * group's color, and a faint white core.
 *
 * An emblem is a list of layers, back to front. A layer's `knockout` shape
 * is filled with the frame's dark before its lines are drawn, hiding the
 * layers behind it there, so overlapping shapes (crossed swords, a hammer
 * behind an anvil) read as one lying over the other.
 */

/**
 * One stroke: a path, or a circle; `width` scales its line weight (detail
 * lines are lighter), `dash` breaks it, and `solid` fills it with the
 * emblem's color (an arrowhead, a point).
 */
export type SkillTreeEmblemStroke =
    | string
    | { readonly d: string; readonly width?: number; readonly solid?: boolean }
    | { readonly circle: readonly [number, number, number]; readonly width?: number; readonly dash?: string; readonly solid?: boolean };

export interface SkillTreeEmblemLayer {
    readonly rotate?: number;
    readonly knockout?: string;
    readonly lines: readonly SkillTreeEmblemStroke[];
}

/** An upright sword, point up, with a cross guard and round pommel; the Melee root crosses two, and Blade Mastery shows one. */
export const SWORD_OUTLINE = 'M0 -24L2.4 -20V4.5H7.2V6.7H1.4V12.7A1.9 1.9 0 1 1 -1.4 12.7V6.7H-7.2V4.5H-2.4V-20Z';
export const SWORD_LINES: readonly SkillTreeEmblemStroke[] = [
    'M0 -24L2.4 -20V4.5H-2.4V-20Z',
    'M-7.2 5.6H7.2',
    'M0 6.7V12.6',
    { d: 'M0 -20.5V3.5', width: 0.6 },
    { circle: [0, 14.6, 1.9] },
];

export const SKILL_TREE_EMBLEMS: Readonly<Record<string, readonly SkillTreeEmblemLayer[]>> = {
    'martial-melee': [
        { rotate: 42, knockout: SWORD_OUTLINE, lines: SWORD_LINES },
        { rotate: -42, knockout: SWORD_OUTLINE, lines: SWORD_LINES },
    ],
    'martial-ranged': [
        {
            lines: [
                'M5 -19Q8 -19 8.5 -16Q15 -8 14.5 0Q15 8 8.5 16Q8 19 5 19',
                'M13 -3.5H16V3.5H13',
                { d: 'M5.6 -18.5L-11 0L5.6 18.5', width: 0.7 },
                'M-11 0H20',
                'M20 0L16.5 -2.4M20 0L16.5 2.4',
                'M-11 0L-14.5 -3.2M-8 0L-11.5 -3.2M-11 0L-14.5 3.2M-8 0L-11.5 3.2',
            ],
        },
    ],
    'martial-defense': [
        {
            lines: [
                'M0 -18L14 -13.5Q14.5 4 0 18.5Q-14.5 4 -14 -13.5Z',
                { d: 'M0 -14.5L10.6 -11Q11 3 0 14.5Q-11 3 -10.6 -11Z', width: 0.8 },
                { d: 'M0 -14.5V14.5', width: 0.7 },
                { circle: [0, -16.2, 0.7] },
                { circle: [9.4, -12.4, 0.7] },
                { circle: [-9.4, -12.4, 0.7] },
                { circle: [10.2, 3, 0.7] },
                { circle: [-10.2, 3, 0.7] },
                { circle: [0, 16, 0.7] },
            ],
        },
    ],
    'essence-fury': [
        {
            lines: [
                'M0 18Q-13 14 -11.5 2Q-10 -6 -3 -11Q-4 -4 1.5 -1.5Q4 -10 0.5 -19Q14 -10 12 3Q10.5 15 0 18Z',
                { d: 'M0 14Q-6.5 11.5 -5.5 4.5Q-4.5 0 -1 -3Q-0.5 2 3.5 4Q6 9.5 0 14Z', width: 0.8 },
                { d: 'M7 -14Q9 -12 8.2 -9', width: 0.7 },
            ],
        },
    ],
    'essence-courage': [
        {
            lines: [
                { circle: [0, 0, 6.5] },
                { d: 'M9.50 0.00L18.50 0.00', width: 1.3 },
                { d: 'M8.23 4.75L12.12 7.00', width: 0.9 },
                { d: 'M4.75 8.23L9.25 16.02', width: 1.3 },
                { d: 'M0.00 9.50L0.00 14.00', width: 0.9 },
                { d: 'M-4.75 8.23L-9.25 16.02', width: 1.3 },
                { d: 'M-8.23 4.75L-12.12 7.00', width: 0.9 },
                { d: 'M-9.50 0.00L-18.50 0.00', width: 1.3 },
                { d: 'M-8.23 -4.75L-12.12 -7.00', width: 0.9 },
                { d: 'M-4.75 -8.23L-9.25 -16.02', width: 1.3 },
                { d: 'M-0.00 -9.50L-0.00 -14.00', width: 0.9 },
                { d: 'M4.75 -8.23L9.25 -16.02', width: 1.3 },
                { d: 'M8.23 -4.75L12.12 -7.00', width: 0.9 },
            ],
        },
    ],
    'essence-sorrow': [
        {
            lines: [
                'M0 -17.5Q11.5 -1.5 10 6Q8.5 15.5 0 15.5Q-8.5 15.5 -10 6Q-11.5 -1.5 0 -17.5Z',
                { d: 'M-4.5 2Q-5.8 8.5 -1 11', width: 0.9 },
            ],
        },
    ],
    'essence-memory': [
        {
            lines: [
                'M-10.5 -17H10.5V-14.5H-10.5Z',
                'M-10.5 14.5H10.5V17H-10.5Z',
                { d: 'M-9.5 -14.5V14.5M9.5 -14.5V14.5', width: 0.8 },
                'M-6.5 -14.5Q-6.5 -4 0 0Q6.5 -4 6.5 -14.5M-6.5 14.5Q-6.5 4 0 0Q6.5 4 6.5 14.5',
                { d: 'M-3.6 -8.5Q0 -6.6 3.6 -8.5', width: 0.8 },
                { d: 'M0 0V11', width: 0.6 },
                { d: 'M-5 14Q0 8.5 5 14', width: 0.8 },
            ],
        },
    ],
    'essence-truth': [
        {
            lines: [
                'M-19 0Q0 -15 19 0Q0 15 -19 0Z',
                { circle: [0, 0, 6.5] },
                { circle: [0, 0, 2.6] },
                { d: 'M0 -15V-12.5M-9 -12.5L-7.6 -10.5M9 -12.5L7.6 -10.5', width: 0.8 },
            ],
        },
    ],
    'essence-resonance': [
        {
            lines: [
                { circle: [0, 0, 2.2] },
                { circle: [0, 0, 7], dash: '30 6' },
                { circle: [0, 0, 12], dash: '38 9 20 9' },
                { circle: [0, 0, 17], dash: '52 10 30 10' },
            ],
        },
    ],
    'crafting-smithing': [
        {
            lines: [
                'M-1 -1L11 -15',
                'M7.3 -19.6L15.6 -11.4M7.3 -19.6L5.5 -17.8L13.8 -9.6L15.6 -11.4',
            ],
        },
        {
            knockout: 'M-13 -3H9Q15 -3 18.5 -6Q16.5 1 9 1.5H6V5Q6 8 9 11H-9Q-6 8 -6 5V1.5H-9Q-13 1.5 -13 -3Z',
            lines: [
                'M-13 -3H9Q15 -3 18.5 -6Q16.5 1 9 1.5H6V5Q6 8 9 11H-9Q-6 8 -6 5V1.5H-9Q-13 1.5 -13 -3Z',
                { d: 'M-11 11H11', width: 1.1 },
            ],
        },
    ],
    'crafting-jewelcrafting': [
        {
            lines: [
                'M-12.5 -12.5H12.5V12.5H-12.5Z',
                { d: 'M-9.5 -9.5H9.5V9.5H-9.5Z', width: 0.7 },
            ],
        },
        {
            knockout: 'M0 -18L13.5 0L0 18L-13.5 0Z',
            lines: [
                'M0 -18L13.5 0L0 18L-13.5 0Z',
                { d: 'M-13.5 0H13.5M0 -18L5 0L0 18L-5 0Z', width: 0.7 },
                { d: 'M-6.5 -9L0 -4.5L6.5 -9', width: 0.6 },
            ],
        },
    ],
};
